
import {
    ApplicationUserModel,
    AuditModel,
    CategoryModel,
    CategoryAlertModel,
    AlertTimingEnum,
    AlertTypeEnum,
    AlertEscalationTypeEnum,
    EscalationTypeEnum,
    PlanItemCloseTypeEnum,
    ProcessTypeEnum,
    ResponseTypeEnum,
    TenantUserBaseModel,
    TenantUserModel,
    CorrectionManagerModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import Page from '@/Page.vue';
import { Component, Ref } from 'vue-property-decorator';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import CorrectionManagerList from '../components/CorrectionManagerList.vue';
import CategoryUserList from '../components/CategoryUserList.vue';
import CategoryCustomFieldGroupList from '../components/CategoryCustomFieldGroupList.vue';
import CategoryAlertsTable from '../components/CategoryAlertsTable.vue';
import { categoriesStore } from '@/libs/categories/+state/store';
import { auditsStore } from '@/libs/audits/+state/store';
import { tenantUsersStore } from '@/libs/tenants/+state/store';
import { SystemAuditValueEnum } from '@/libs/core/models/system-audit-value-enum';
import { ProductAuditValueEnum } from '@/libs/core/models/product-audit-value-enum';
import NDialog from '@/libs/common/components/NDialog.vue';
import { planItemsStore } from '@/libs/plan-items/+state/store';

@Component({
    components: {
        CorrectionManagerList,
        CategoryUserList,
        NDialog,
        CategoryCustomFieldGroupList,
        CategoryAlertsTable,
    },
})
export default class CategoryDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;
    loading = false;
    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    category: CategoryModel | null = null;
    deleteDialog = false;
    deleting = false;
    canDelete = false;
    useDefinedExplanation = false;
    basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];
    maskRules = [(v: any) => this.validateAuditMask(v)];
    tenantUsersOfCategory: TenantUserBaseModel[] = [];
    correctionManagers: CorrectionManagerModel[] = [];
    @Ref() form!: any;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected ResponseTypeEnum: typeof ResponseTypeEnum = ResponseTypeEnum;
    protected AlertTypeEnum: typeof AlertTypeEnum = AlertTypeEnum;
    protected AlertTimingEnum: typeof AlertTimingEnum = AlertTimingEnum;
    showMaskHelp = false;
    hasCorrectionManagerChanges = false;
    managerChangeDialog = false;
    applyManagersToExistingFindings: boolean | null = null;
    pendingCategoryToSave: CategoryModel | null = null;
    alertsRecipientsRules = [(v: any) => v === true || (this.$t('validation.thisFieldIsRequired') as string)];
    unsavedAlertsDialog = false;
    originalCategoryAlerts: CategoryAlertModel[] = [];

    get isNewItem(): boolean {
        return this.category?.id == Guid.EmptyGuid();
    }

    alertsRecipientsValid(): boolean {
        const alerts = this.category?.categoryAlerts ?? [];
        if (!alerts || alerts.length === 0) {
            return true;
        }
        return alerts.every((a) => (a.alertEscalations ?? []).length > 0);
    }

    get useTpm(): boolean {
        return (window as any).UseTpm ?? false;
    }

    isProcessTypeAudit(): boolean {
        return this.category?.processType == ProcessTypeEnum.Audit;
    }

    validateAuditMask(mask: string): boolean | string {
        // Povolené základní tokeny
        const allowedTokens = ['{YYYY}', '{MM}', '{DD}', '{SEQ}'];
        // Regex pro padding sekvenci
        const paddingRegex = /\{SEQ:([^:]):(\d+)\}/g;
        // Regex pro všechny složené závorky
        const allTokenRegex = /\{[^}]+\}/g;

        // Musí obsahovat sekvenci
        if (!mask.match(/\{SEQ(:(.):\d+)?\}/)) {
            return this.$i18n.t('categories.autoEvidenceNumberMaskSeq').toString();
        }

        // Zkontroluj všechny padding sekvence
        let match;
        while ((match = paddingRegex.exec(mask)) !== null) {
            const padChar = match[1];
            const padLen = Number(match[2]);
            if (!padChar || padChar.length !== 1 || isNaN(padLen) || padLen < 1) {
                return `${this.$i18n.t('categories.autoEvidenceNumberMaskBadPadding')}${match}`;
            }
        }

        // Zkontroluj všechny zástupné znaky
        const tokens = mask.match(allTokenRegex) || [];
        for (const token of tokens) {
            if (!allowedTokens.includes(token) && !/^\{SEQ:.{1}:\d+\}$/.test(token)) {
                return `${this.$i18n.t('categories.autoEvidenceNumberMaskBadToken')}${token}`;
            }
        }

        return true;
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        if (this.hasCategoryAlertsChanges()) {
            this.unsavedAlertsDialog = true;
            return;
        }
        this.dialog = false;
    }

    confirmCloseWithUnsavedChanges() {
        this.unsavedAlertsDialog = false;
        this.dialog = false;
    }

    hasCategoryAlertsChanges(): boolean {
        if (!this.category || this.isNewItem) {
            return false;
        }

        const currentAlerts = this.category.categoryAlerts ?? [];
        const originalAlerts = this.originalCategoryAlerts ?? [];

        return JSON.stringify(currentAlerts) !== JSON.stringify(originalAlerts);
    }

    deleteClick() {
        auditsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<AuditModel>(
                undefined,
                [
                    {
                        field: 'categoryId',
                        op: 'eq',
                        comparand: this.category!.id!,
                    },
                ],
                undefined,
                false,
                'CategoryDelete'
            )
        );
        this.subscribe(auditsStore, CrudReponse.GetAll, 'CategoryDelete').then((e: AuditModel[]) => {
            this.canDelete = e.length == 0;
            this.deleteDialog = true;
        });
    }

    deleteCategory() {
        this.deleting = true;
        categoriesStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(categoriesStore, CrudReponse.Delete)
            .then((e: any) => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch((e: any) => {
                this.deleting = false;
            });
    }

    responseTypeChanged() {
        if (this.category!.responseType == ResponseTypeEnum.Ok) {
            this.category!.responseValues = [{ value: 1, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation }];
        } else if (this.category!.responseType == ResponseTypeEnum.OkNok) {
            this.category!.responseValues = [
                { value: 1, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
                { value: 3, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.OkNokWarning) {
            this.category!.responseValues = [
                { value: 1, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
                { value: 2, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.NokWarning) {
            this.category!.responseValues = [
                { value: 2, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreZeroToFive) {
            this.category!.responseValues = [
                { value: 0, color: '9e0b0b', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 1, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 2, color: 'e0890d', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 4, color: 'a2eba2', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 5, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreOneToFive) {
            this.category!.responseValues = [
                { value: 1, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 2, color: 'e0890d', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: 'deb40b', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 4, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 5, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreZeroToFour) {
            this.category!.responseValues = [
                { value: 0, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 1, color: 'e0890d', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 2, color: 'deb40b', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 4, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreZeroToOne) {
            this.category!.responseValues = [
                { value: 0, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 1, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreZeroToTwo) {
            this.category!.responseValues = [
                { value: 0, color: '1fbf1f', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 1, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 2, color: 'e61717', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreOneToThree) {
            this.category!.responseValues = [
                { value: 1, color: '1fbf1f', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 2, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: 'e61717', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreZeroToThree) {
            this.category!.responseValues = [
                { value: 0, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 1, color: 'e0890d', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 2, color: 'deb40b', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 3, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ScoreOneToFour) {
            this.category!.responseValues = [
                { value: 1, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 2, color: 'e0890d', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 3, color: 'deb40b', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 4, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.VDA63) {
            this.category!.responseValues = [
                { value: 0, color: 'e61717', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 4, color: 'e0890d', escalationType: EscalationTypeEnum.RequiredEscalation },
                { value: 6, color: 'deb40b', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 8, color: 'e2e607', escalationType: EscalationTypeEnum.OptionalEscalation },
                { value: 10, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.SystemAudit) {
            this.category!.responseValues = [
                {
                    value: SystemAuditValueEnum.Compliant,
                    color: '1fbf1f',
                    escalationType: EscalationTypeEnum.NoEscalation,
                },
                {
                    value: SystemAuditValueEnum.MajorNotCompliant,
                    color: 'e61717',
                    escalationType: EscalationTypeEnum.RequiredEscalation,
                },
                {
                    value: SystemAuditValueEnum.MinorNotCompliant,
                    color: 'e0890d',
                    escalationType: EscalationTypeEnum.RequiredEscalation,
                },
                {
                    value: SystemAuditValueEnum.OportunityForImprovement,
                    color: 'e2e607',
                    escalationType: EscalationTypeEnum.OptionalEscalation,
                },
                {
                    value: SystemAuditValueEnum.PositiveAspect,
                    color: 'deb40b',
                    escalationType: EscalationTypeEnum.NoEscalation,
                },
            ];
        } else if (this.category!.responseType == ResponseTypeEnum.ProductAudit) {
            this.category!.responseValues = [
                { value: ProductAuditValueEnum.OK, color: '1fbf1f', escalationType: EscalationTypeEnum.NoEscalation },
                {
                    value: ProductAuditValueEnum.A,
                    color: 'e61717',
                    escalationType: EscalationTypeEnum.RequiredEscalation,
                },
                {
                    value: ProductAuditValueEnum.B,
                    color: 'e61717',
                    escalationType: EscalationTypeEnum.RequiredEscalation,
                },
                {
                    value: ProductAuditValueEnum.C,
                    color: 'e2e607',
                    escalationType: EscalationTypeEnum.RequiredEscalation,
                },
            ];
        }
    }

    useNotRatedOptionChanged() {
        if (this.category!.useNotRatedOption) {
            this.category!.enforceReasonForNotRatedOption = true;
        }
    }

    private load(): Promise<any> {
        if (this.id != Guid.EmptyGuid()) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                this.loadCategory()
                    .then((category: CategoryModel) => {
                        this.correctionManagers = [...(category.correctionManagers ?? [])];
                        this.loadTenantUsersWithAllCategories()
                            .then((tenantUsers: TenantUserModel[]) => {
                                this.tenantUsersOfCategory = (
                                    category.categoryUsers?.select((x: any) => x.tenantUser).toArray() ?? []
                                ).concat(tenantUsers);
                                this.tenantUsersOfCategory = this.tenantUsersOfCategory
                                    .groupBy((x) => x.id!)
                                    .select((x) => x.first())
                                    .orderBy((x) => x.user?.displayName ?? '')
                                    .toArray();
                                this.loading = false;
                                resolve(category);
                            })
                            .catch((e: any) => {
                                reject(e);
                            });
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            });
        } else {
            this.tenantUsersOfCategory = [];
            this.category = {
                id: Guid.EmptyGuid(),
                name: '',
                correctionManagers: [],
                responseValues: [],
                categoryAlerts: [],
                useExplanationByCategory: false,
                useCommentOnResponse: true,
                showCommentOnlyForOkResponse: false,
                categoryUsers: [],
                useNotRatedOption: false,
                enforceReasonForNotRatedOption: true,
                planItemCloseType: PlanItemCloseTypeEnum.ByAuditor,
                processType: ProcessTypeEnum.Audit,
                findingDefaultProcessDays: null,
                showNotClosedMeasuresInAudit: false,
                isSupplierAudit: false,
                categoryCustomFieldGroups: [],
                mustSelectFindingManager: false,
                useCustomNotRatedReason: true,
                useStation: false,
                useShiftType: true,
            } as CategoryModel;
            // Resetovat originalCategoryAlerts pro novou kategorii
            this.originalCategoryAlerts = [];
            return new Promise((resolve, reject) => {
                resolve(null);
            });
        }
    }

    private loadCategory(): Promise<CategoryModel> {
        return new Promise((resolve, reject) => {
            categoriesStore.dispatch(CrudAction.Get, { id: this.id });
            this.subscribe(categoriesStore, CrudReponse.Get)
                .then((e) => {
                    this.category = e;
                    // Uložit původní stav categoryAlerts pro detekci změn
                    this.originalCategoryAlerts = JSON.parse(JSON.stringify(e.categoryAlerts ?? []));
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private loadTenantUsersWithAllCategories(): Promise<TenantUserModel[]> {
        return new Promise((resolve, reject) => {
            tenantUsersStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<TenantUserModel>(
                    [],
                    [
                        {
                            field: 'accessAllCategories',
                            op: 'eq',
                            comparand: true,
                        },
                    ],
                    undefined,
                    false,
                    'TenantUsersWithAllCategories'
                )
            );
            this.subscribe(tenantUsersStore, CrudReponse.GetAll, 'TenantUsersWithAllCategories')
                .then((e) => {
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private save() {
        const formOk = this.form.validate();
        const recipientsValid = this.alertsRecipientsValid();
        if (!(formOk && recipientsValid)) {
            this.$forceUpdate();
            return;
        }
        if (formOk) {
            const category = { ...this.category };
            category.correctionManagers = category
                .correctionManagers!.where((x) => x.tenantUserId != null && x.tenantUserId != Guid.EmptyGuid())
                .toArray();
            this.saving = true;
            if (this.id == Guid.EmptyGuid()) {
                categoriesStore.dispatch(CrudAction.Create, { item: category });
                this.subscribe(categoriesStore, CrudReponse.Create)
                    .then(() => {
                        this.load().then(() => {
                            this.saving = false;
                            // Resetovat stav categoryAlerts po úspěšném uložení
                            this.originalCategoryAlerts = JSON.parse(JSON.stringify(this.category?.categoryAlerts ?? []));
                            this.close();
                        });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            } else {
                const originalManagers = (this.correctionManagers ?? [])
                    .where((x) => x.tenantUserId != null && x.tenantUserId != Guid.EmptyGuid())
                    .select((x) => x.tenantUserId)
                    .toArray();
                const updatedManagers = (category.correctionManagers ?? []).select((x) => x.tenantUserId).toArray();
                const originalSet = new Set(originalManagers.filter((x) => !!x));
                const updatedSet = new Set(updatedManagers.filter((x) => !!x));
                const hasAdded = Array.from(updatedSet).some((id) => !originalSet.has(id as string));
                const hasRemoved = Array.from(originalSet).some((id) => !updatedSet.has(id as string));
                this.hasCorrectionManagerChanges = hasAdded || hasRemoved;
                if (this.hasCorrectionManagerChanges) {
                    this.pendingCategoryToSave = category;
                    this.managerChangeDialog = true;
                    this.saving = false;
                    return;
                }
                categoriesStore.dispatch(CrudAction.Update, { item: category });
                this.subscribe(categoriesStore, CrudReponse.Update)
                    .then(() => {
                        this.load().then(() => {
                            this.saving = false;
                            // Resetovat stav categoryAlerts po úspěšném uložení
                            this.originalCategoryAlerts = JSON.parse(JSON.stringify(this.category?.categoryAlerts ?? []));
                            this.close();
                        });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            }
        }
    }

    private confirmApplyManagersChanges(applyToExisting: boolean) {
        this.applyManagersToExistingFindings = applyToExisting;
        this.managerChangeDialog = false;
        if (!this.pendingCategoryToSave) {
            return;
        }
        const category = this.pendingCategoryToSave;
        this.pendingCategoryToSave = null;
        this.saving = true;
        categoriesStore.dispatch(CrudAction.Update, {
            item: category,
            headers: {
                Action: 'ApplyManagersChanges',
            },
        });
        this.subscribe(categoriesStore, CrudReponse.Update)
            .then(() => {
                this.load().then(() => {
                    this.saving = false;
                    // Resetovat stav categoryAlerts po úspěšném uložení
                    this.originalCategoryAlerts = JSON.parse(JSON.stringify(this.category?.categoryAlerts ?? []));
                    this.close();
                });
            })
            .catch((e) => {
                this.saving = false;
                throw e;
            });
    }
}
